import { formatNumber } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import type { ColumnDef } from '@tanstack/react-table'
import { type FC, memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import Table, { TableHeader } from 'components/Table'
import { Body } from 'components/Text'
import { NO_VALUE } from 'constants/placeholder'
import { blockExplorer } from 'containers/Connector/Connector'
import isEqual from 'lodash/isEqual'
import { fetchGlobalTradeHistory } from 'state/futures/actions'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import {
	selectGlobalTradeHistoryTableData,
	selectLoadingGloablHistory,
} from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { calculateTimeDelta } from 'utils/dates'

type TradesHistoryTableProps = {
	mobile?: boolean
	display?: boolean
	containerHeight: number
}

enum TableColumnAccessor {
	Amount = 'amount',
	Price = 'price',
	Time = 'time',
}

const GlobalTradesHistoryTable: FC<TradesHistoryTableProps> = memo(
	({ mobile, display, containerHeight }) => {
		const { t } = useTranslation()
		const dispatch = useAppDispatch()
		const perpsProvider = useAppSelector(selectPerpsProvider)
		const data = useAppSelector(selectGlobalTradeHistoryTableData, isEqual)
		const isLoading = useAppSelector((state) => {
			const loading = selectLoadingGloablHistory(state)
			const trades = selectGlobalTradeHistoryTableData(state)
			if (trades.length > 0) return false
			return loading
		})

		const fetchNextPage = useCallback(() => {
			if (isLoading) return

			if (data.length < 50) return

			return dispatch(fetchGlobalTradeHistory({ providers: [perpsProvider], nextPage: true }))
		}, [isLoading, perpsProvider, data.length, dispatch])

		const calTimeDelta = useCallback((time: number) => calculateTimeDelta(time, t), [t])

		const columns = useMemo<ColumnDef<(typeof data)[0]>[]>(
			() => [
				{
					header: () => <TableHeader>{t('futures.market.history.amount-label')}</TableHeader>,
					accessorKey: TableColumnAccessor.Amount,
					cell: (cellProps) => {
						const normal = cellProps.row.original.orderType === 'Liquidation'
						const negative = wei(cellProps.row.original.amount).gt(0)
						return (
							<DirectionalValue negative={negative} normal={normal}>
								{formatNumber(wei(cellProps.row.original.amount).abs(), {
									suggestDecimals: true,
									truncateOver: 1e6,
									maxDecimals: 6,
								})}{' '}
								{normal ? '💀' : ''}
							</DirectionalValue>
						)
					},
					size: 110,
				},
				{
					header: () => <TableHeader>{t('futures.market.history.price-label')}</TableHeader>,
					accessorKey: TableColumnAccessor.Price,
					cell: (cellProps) => {
						return (
							<PriceValue>
								${formatNumber(cellProps.row.original.value, { suggestDecimals: true })}
							</PriceValue>
						)
					},
					size: 100,
				},
				{
					header: () => <TableHeader>{t('futures.market.history.time-label')}</TableHeader>,
					accessorKey: TableColumnAccessor.Time,
					cell: (cellProps) => {
						return <TimeValue>{calTimeDelta(cellProps.row.original.time)}</TimeValue>
					},
					size: 70,
				},
			],
			[t, calTimeDelta]
		)
		return (
			<HistoryContainer $display={mobile || display} mobile={mobile}>
				<StyledTable
					data={data}
					isLoading={isLoading}
					onTableRowClick={(_row) => {
						return _row.original.id !== NO_VALUE && _row.original.ownerAddress
							? window.open(`${blockExplorer.addressLink(_row.original.ownerAddress)}`)
							: undefined
					}}
					noRowBorder
					highlightRowsOnHover
					rounded={false}
					columns={columns}
					containerHeight={containerHeight}
					useVirtual
					fetchNextPage={fetchNextPage}
				/>
			</HistoryContainer>
		)
	}
)

export default GlobalTradesHistoryTable

const HistoryContainer = styled.div<{ mobile?: boolean; $display?: boolean }>`
	box-sizing: border-box;
	border-left: ${(props) => props.theme.colors.selectedTheme.border};
	height: 100%;
	width: 100%;
	${(props) =>
		props.mobile &&
		css`
			height: 100%;
			margin-bottom: 0;
			border-radius: 0;
			border: none;
			border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
		`};

	${(props) =>
		!props.$display &&
		css`
			display: none;
		`}
`

const TableAlignment = css`
	justify-content: space-between;
	& > div:first-child {
		flex: 70 70 0 !important;
	}
	& > div:nth-child(2) {
		flex: 100 100 0 !important;
		justify-content: center;
	}
	& > div:last-child {
		flex: 60 60 0 !important;
		justify-content: flex-end;
		padding-right: 20px;
	}
`

const StyledTable = styled(Table)`
	border: none !important;

	.table-row,
	.table-body-row {
		${TableAlignment}
		padding: 0;
	}
	.table-body-cell {
		height: 28px;
	}
` as typeof Table

const PriceValue = styled(Body).attrs({ mono: true })`
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	padding-left: 5px;
`

const TimeValue = styled(Body)`
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
`

const DirectionalValue = styled(PriceValue)<{ negative?: boolean; normal?: boolean }>`
	white-space: nowrap;
	padding-left: 0;
	color: ${(props) =>
		props.normal
			? props.theme.colors.selectedTheme.button.text.primary
			: props.theme.colors.selectedTheme[props.negative ? 'green' : 'red']};
`
